/**
 * HLF Transfer Utility
 * 處理跨平台的 CHNX 代幣轉賬
 * VERSION: 2024-12-18 22:45:00
 */

// Escrow 地址
export const ESCROW_ADDRESS = '0x80C1A3C9a0a2fCB14516F97959260527702822A8';

// Version tag - used to confirm new code is loaded
const VERSION = '2024-12-18 22:45:00';
console.log('==============================================');
console.log('transfer.ts loaded - VERSION:', VERSION);
console.log('==============================================');

/**
 * 檢測平台類型
 */
export const detectPlatform = () => {
  const hasAndroidBridge = !!(window as any).AndroidWeb3Bridge;
  const hasEthereum = !!(window as any).ethereum;
  
  // ✅ 優先檢測 AndroidWeb3Bridge（原生 Android WebView）
  const isAndroid = hasAndroidBridge && typeof (window as any).AndroidWeb3Bridge.hlfTransfer === 'function';
  
  // ✅ 如果不是 Android，再檢查 MetaMask
  const isMetaMask = !isAndroid && hasEthereum;
  
  console.log('🔍 Platform Detection:', {
    hasAndroidBridge,
    hasEthereum,
    hasHlfTransfer: typeof (window as any).AndroidWeb3Bridge?.hlfTransfer,
    isAndroid,
    isMetaMask
  });
  
  return {
    isAndroid,
    isMetaMask,
    isSupported: isAndroid || isMetaMask
  };
};

/**
 * 轉賬到 Escrow (Android)
 */
export const transferViaAndroid = (toAddress: string, amount: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const callbackId = 'transfer_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    console.log('==============================================');
    console.log('🚀 [Android Transfer] VERSION:', VERSION);
    console.log('🚀 [Android Transfer] ===== STARTING TRANSFER =====');
    console.log('🚀 [Android Transfer] Callback ID:', callbackId);
    console.log('🚀 [Android Transfer] To Address:', toAddress);
    console.log('🚀 [Android Transfer] Amount:', amount);
    console.log('🚀 [Android Transfer] Bridge exists:', !!(window as any).AndroidWeb3Bridge);
    console.log('🚀 [Android Transfer] hlfTransfer exists:', typeof (window as any).AndroidWeb3Bridge?.hlfTransfer);
    
    // 設置成功回調
    (window as any)[callbackId] = (txHash: string) => {
        console.log('[Android Transfer] Callback received, txHash:', txHash);
      
      // Validate txHash
      if (!txHash || txHash === 'undefined' || txHash === 'null' || txHash.trim() === '') {
        console.error('[Android Transfer] Invalid txHash:', txHash);
        delete (window as any)[callbackId];
        delete (window as any)[callbackId + '_error'];
        reject(new Error('Android returned invalid transaction hash'));
        return;
      }
      
      console.log('[Android Transfer] Transfer successful, txHash:', txHash);
      delete (window as any)[callbackId];
      delete (window as any)[callbackId + '_error'];
      resolve(txHash);
    };
    
    // Set error callback
    (window as any)[callbackId + '_error'] = (error: string) => {
      console.error('❌ Android transfer callback error:', error);
      delete (window as any)[callbackId];
      delete (window as any)[callbackId + '_error'];
      reject(new Error(error || 'Transfer failed'));
    };
    
    try {
      // 調用 Android 橋接
      console.log('[Android Transfer] Calling AndroidWeb3Bridge.hlfTransfer');
      console.log('[Android Transfer] toAddress:', toAddress);
      console.log('[Android Transfer] amount:', amount);
      console.log('[Android Transfer] callbackId:', callbackId);
      
      if (!(window as any).AndroidWeb3Bridge) {
        throw new Error('AndroidWeb3Bridge not found');
      }
      
      if (typeof (window as any).AndroidWeb3Bridge.hlfTransfer !== 'function') {
        throw new Error('hlfTransfer method not found on AndroidWeb3Bridge');
      }
      
      (window as any).AndroidWeb3Bridge.hlfTransfer(
        toAddress,
        amount.toString(),
        callbackId
      );
      
      console.log('[Android Transfer] hlfTransfer invoked, waiting for callback...');
    } catch (e: any) {
      console.error('[Android Transfer] Error:', e);
      delete (window as any)[callbackId];
      delete (window as any)[callbackId + '_error'];
      reject(e);
    }
    
    // 超時保護 (30 秒)
    setTimeout(() => {
      if ((window as any)[callbackId]) {
        console.error('⏱️ [Android Transfer] ===== TIMEOUT =====');
        console.error('⏱️ [Android Transfer] Waited 30 seconds but Android never called callback');
        console.error('⏱️ [Android Transfer] Callback ID:', callbackId);
        console.error('⏱️ [Android Transfer] Expected: window.' + callbackId + '(txHash)');
        console.error('⏱️ [Android Transfer] Possible reasons:');
        console.error('⏱️ [Android Transfer]   1. Android transfer failed silently');
        console.error('⏱️ [Android Transfer]   2. Android callback not calling JavaScript');
        console.error('⏱️ [Android Transfer]   3. Callback ID mismatch');
        delete (window as any)[callbackId];
        delete (window as any)[callbackId + '_error'];
        reject(new Error('Transfer timeout after 30 seconds. Android did not respond.'));
      }
    }, 30000);
  });
};

/**
 * 轉賬到 Escrow (MetaMask - 未來實現)
 */
export const transferViaMetaMask = async (_toAddress: string, _amount: string): Promise<string> => {
  throw new Error('MetaMask support coming soon. Please use GiGi Wallet App.');
  
  // TODO: 未來實現 MetaMask 轉賬
  // const provider = new ethers.providers.Web3Provider(window.ethereum);
  // const signer = provider.getSigner();
  // const tx = await signer.sendTransaction({
  //   to: _toAddress,
  //   value: ethers.utils.parseEther(_amount)
  // });
  // await tx.wait();
  // return tx.hash;
};

/**
 * 統一的轉賬接口
 */
export const transferToEscrow = async (amount: string): Promise<string> => {
  console.log('🔄 [Transfer] Starting transferToEscrow:', {
    amount,
    escrow: ESCROW_ADDRESS
  });
  
  const platform = detectPlatform();
  
  console.log('🔄 [Transfer] Platform detected:', platform);
  
  if (!platform.isSupported) {
    const error = 'No wallet provider detected. Please use GiGi Wallet App or MetaMask.';
    console.error('❌ [Transfer]', error);
    throw new Error(error);
  }
  
  if (platform.isAndroid) {
    console.log('📱 [Transfer] Using Android wallet');
    return await transferViaAndroid(ESCROW_ADDRESS, amount);
  } else if (platform.isMetaMask) {
    console.log('💻 [Transfer] Using MetaMask');
    return await transferViaMetaMask(ESCROW_ADDRESS, amount);
  } else {
    const error = 'Unsupported wallet provider';
    console.error('❌ [Transfer]', error);
    throw new Error(error);
  }
};

/**
 * 格式化交易哈希用於顯示
 */
export const formatTxHash = (txHash: string): string => {
  if (txHash.length <= 16) return txHash;
  return `${txHash.slice(0, 8)}...${txHash.slice(-8)}`;
};

